import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import firebase from 'firebase/app';
import 'firebase/database';

/**
 * Per-tenant feature flags. Add a new flag here, then default it under
 * `defaultFeatures` and override in Firebase RTDB at settings/bitrixUrl/<tenant>/features
 * to roll it out per agent.
 */
export interface TenantFeatures {
  /** Show HMR-specific business fields (letters-stand-for, share-capital options, etc.) */
  hmrBusinessFields: boolean;
  /** Require corporate KYC documents (cert of incorp, annual return, M&A, etc.) */
  requireCorporateKYC: boolean;
  /** Require beneficial owner declaration */
  requireBeneficialOwner: boolean;
  /** Require residential proof for individuals */
  requireResidentialProof: boolean;
  /** Route /start → /apply (new stepper UI). false → legacy single-page form. */
  useNewStepper: boolean;
}

export interface TenantConfig {
  key: string;
  displayName: string;
  subdomain: string;
  bitrixUrl: string;
  responsible: string;
  cosecid: string;
  logo: string;
  brandColor?: string;
  businessNatureFile?: string;
  features: TenantFeatures;
}

const defaultFeatures: TenantFeatures = {
  hmrBusinessFields: false,
  requireCorporateKYC: false,
  requireBeneficialOwner: false,
  requireResidentialProof: false,
  useNewStepper: false
};

// Hostnames that should opt in to the new stepper + HMR feature set by default.
// Once the new UI is rolled out to all tenants, this list can be deleted.
const NEW_UI_HOSTS = new Set([
  'cosec-testing.softon.io',
  'localhost',
  '127.0.0.1'
]);

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly tenant$ = new BehaviorSubject<TenantConfig | null>(null);

  /** Emits the resolved tenant config once available. */
  readonly current$: Observable<TenantConfig> = this.tenant$
    .asObservable()
    .pipe(filter((t): t is TenantConfig => t !== null));

  /** Synchronous accessor — only safe after waitReady(). */
  get current(): TenantConfig | null {
    return this.tenant$.value;
  }

  /** Resolve a single field with safe defaults (e.g. on first paint). */
  feature<K extends keyof TenantFeatures>(key: K): TenantFeatures[K] {
    return (this.tenant$.value?.features ?? defaultFeatures)[key];
  }

  /** Wait until tenant config is loaded before proceeding (use in guards). */
  waitReady(): Promise<TenantConfig> {
    return this.current$.pipe(take(1)).toPromise() as Promise<TenantConfig>;
  }

  /**
   * Load tenant config based on the current host. Falls back to a "testing"
   * tenant entry for localhost/web.app/firebaseapp.com.
   *
   * Settings node in Firebase RTDB: settings/bitrixUrl/<tenantKey>
   *   { subdomain, bitrixUrl, responsible, cosecid, logo, title, brandColor, features? }
   */
  async load(): Promise<TenantConfig> {
    const host = window.location.host;
    const snapshot = await firebase.database().ref('settings/bitrixUrl').once('value');
    const settingsMap = snapshot.val() || {};
    const entries = Object.entries<any>(settingsMap);

    // 1. Exact subdomain match
    let match = entries.find(([_, cfg]) => {
      const sub = (cfg?.subdomain || '').toString();
      return sub === host || (sub && host.startsWith(sub + '.'));
    });

    // 2. Fallback for local/preview hosts
    if (!match && (
      host.includes('localhost') ||
      host.includes('127.0.0.1') ||
      host.includes('web.app') ||
      host.includes('firebaseapp.com') ||
      host.includes('softon.io')
    )) {
      const testingEntry = settingsMap.testing
        || entries.find(([_, cfg]) => (cfg?.subdomain || '').includes('testing'))?.[1];
      match = testingEntry ? ['testing', testingEntry] : undefined;
    }

    const [key, raw] = match ?? ['unknown', {} as any];

    const config: TenantConfig = {
      key,
      displayName: raw?.title || key,
      subdomain: raw?.subdomain || host,
      bitrixUrl: (raw?.bitrixUrl || '').toString(),
      responsible: (raw?.responsible || '').toString(),
      cosecid: (raw?.cosecid || '').toString(),
      logo: raw?.logo || '',
      brandColor: raw?.brandColor || '#003366',
      businessNatureFile: raw?.businessNatureFile
        || (NEW_UI_HOSTS.has(host) ? 'assets/business_nature/business_nature_hmr.json'
                                   : 'assets/business_nature/business_nature.json'),
      features: {
        ...defaultFeatures,
        // Auto-enable HMR + new stepper for cosec-testing/localhost until other
        // tenants opt in via Firebase settings.
        hmrBusinessFields: NEW_UI_HOSTS.has(host),
        requireCorporateKYC: NEW_UI_HOSTS.has(host),
        requireBeneficialOwner: NEW_UI_HOSTS.has(host),
        requireResidentialProof: NEW_UI_HOSTS.has(host),
        useNewStepper: NEW_UI_HOSTS.has(host),
        ...(raw?.features || {})
      }
    };

    this.tenant$.next(config);

    // Apply brand color to CSS custom properties so SCSS can pick them up.
    if (config.brandColor) {
      document.documentElement.style.setProperty('--tenant-brand', config.brandColor);
    }
    if (config.logo) {
      document.documentElement.style.setProperty('--tenant-logo', `url("${config.logo}")`);
    }

    return config;
  }
}
